import * as yup from 'yup';
import {
  TransferAssetFlowContextValue,
  TransferAssetFlowStep,
} from '@providers/TransferAssetFlowProvider';
import {
  isAmountZeroEquivalent,
  IsNativeToken,
  minBalanceForSymbol,
} from '@lib/utils';
import { isAddress } from '@lib/utils';
import { Network } from '@types';

export enum ValidationField {
  amount = 'amount',
  toAddress = 'toAddress',
  toNetwork = 'toNetwork',
  fromNetwork = 'fromNetwork',
  tokenAsset = 'tokenAsset',
  tokenSymbol = 'tokenSymbol',
  tokenBalance = 'tokenBalance',
}

const PROVIDE_DETAILS_VALIDATION_SCHEMA = yup.object().shape({
  [ValidationField.amount]: yup
    .string()
    .required('missing_amount')
    .test({
      name: 'is_non_zero',
      test: (v) => {
        if (!v) {
          return false;
        }

        return !isAmountZeroEquivalent(v);
      },
      message: 'invalid_amount',
    })
    .test({
      name: 'is_less_or_equal_than_balance',
      test: function (v) {
        const tokenBalance: string = this.resolve(
          yup.ref(ValidationField.tokenBalance)
        );

        return !Number.isNaN(tokenBalance) && Number(v) <= Number(tokenBalance);
      },
      message: 'greater_than_balance',
    })
    .test({
      name: 'has_enough_for_gas_fee',
      test: function (v) {
        // const tokenAsset: string = this.resolve(
        //   yup.ref(ValidationField.tokenAsset)
        // );
        const tokenSymbol: string = this.resolve(
          yup.ref(ValidationField.tokenSymbol)
        );
        const fromNetwork: Network = this.resolve(
          yup.ref(ValidationField.fromNetwork)
        );
        const tokenBalance: string = this.resolve(
          yup.ref(ValidationField.tokenBalance)
        );
        // don't check if not native
        if (!IsNativeToken({ network: fromNetwork, symbol: tokenSymbol }))
          return true;
        const minBalance = minBalanceForSymbol(tokenSymbol);
        return (
          parseFloat((Number(tokenBalance) - Number(v)).toFixed(6)) >=
          minBalance
        );
      },
      message: 'less_or_equal_than_minimum',
    }),
  [ValidationField.toAddress]: yup
    .string()
    .trim()
    .test({
      name: 'valid_address_for_destination_network',
      test: function (toAddress) {
        if (toAddress) {
          const toNetwork: Network = this.resolve(
            yup.ref(ValidationField.toNetwork)
          );

          return isAddress(toAddress, toNetwork);
        }
        return false;
      },
      message: 'invalid_address_format',
    })
    .required('missing_destination_address'),
  [ValidationField.tokenAsset]: yup.string().required('missing_token'),
  [ValidationField.tokenBalance]: yup.string(),
});

export type UseValidationErrorList = {
  [key in ValidationField]?: string[];
};

export type UseValidationErrorState = {
  ok: boolean;
  errors?: UseValidationErrorList;
};

function formatStateForSchema(
  step: TransferAssetFlowStep,
  state: TransferAssetFlowContextValue['state']
) {
  if (step === TransferAssetFlowStep.ProvideDetails) {
    return {
      [ValidationField.amount]: state.amount,
      [ValidationField.toAddress]: state.toAddress,
      [ValidationField.tokenAsset]: state.tokenAsset?.name,
      [ValidationField.tokenSymbol]: state.tokenAsset?.symbol,
      [ValidationField.tokenBalance]: state.tokenBalance,
      [ValidationField.toNetwork]: state.toNetwork,
      [ValidationField.fromNetwork]: state.fromNetwork,
    };
  }

  return {};
}

interface ValidateProvideDetailsStepParams {
  step: TransferAssetFlowStep.ProvideDetails;
  fields: Array<'amount' | 'toAddress' | 'tokenAsset' | 'tokenBalance'> | null;
}

export type ValidateParams = ValidateProvideDetailsStepParams;

export type ValidateResult = {
  errors: UseValidationErrorList;
  ok: boolean;
};

function useValidateTransferAssetFlow(): (
  params: ValidateParams,
  state: TransferAssetFlowContextValue['state']
) => ValidateResult {
  function getValidationSchema(
    step: TransferAssetFlowStep
  ): typeof PROVIDE_DETAILS_VALIDATION_SCHEMA | null {
    if (step === TransferAssetFlowStep.ProvideDetails) {
      return PROVIDE_DETAILS_VALIDATION_SCHEMA;
    }

    return null;
  }

  function validate(
    { step, fields }: ValidateParams,
    state: TransferAssetFlowContextValue['state']
  ): ValidateResult {
    if (!fields) {
      return validateStep(step, state);
    }

    return validateFieldsInStep(step, fields, state);
  }

  function validateStep(
    step: TransferAssetFlowStep,
    state: TransferAssetFlowContextValue['state']
  ): ValidateResult {
    const schema = getValidationSchema(step);

    if (schema) {
      try {
        schema.validateSync(formatStateForSchema(step, state), {
          abortEarly: false,
        });
        return { ok: true, errors: {} };
      } catch (err) {
        const errors: UseValidationErrorList = {};

        (err as yup.ValidationError).inner.forEach(
          (error: yup.ValidationError) => {
            const fieldKey = error.path as ValidationField;
            errors[fieldKey] = errors[fieldKey] || [];
            errors[fieldKey]!.push(error.message);
          }
        );

        return { ok: false, errors };
      }
    }

    return { ok: true, errors: {} };
  }

  function validateFieldsInStep(
    step: TransferAssetFlowStep,
    fields: string[],
    state: TransferAssetFlowContextValue['state']
  ): ValidateResult {
    const schema = getValidationSchema(step);

    if (schema) {
      try {
        schema.pick(fields).validateSync(formatStateForSchema(step, state), {
          abortEarly: false,
        });

        return { ok: true, errors: {} };
      } catch (err) {
        const errors: UseValidationErrorList = {};

        (err as yup.ValidationError).inner.forEach(
          (error: yup.ValidationError) => {
            const fieldKey = error.path as ValidationField;
            errors[fieldKey] = errors[fieldKey] || [];
            errors[fieldKey]!.push(error.message);
          }
        );

        return { ok: false, errors };
      }
    }

    return { ok: true, errors: {} };
  }

  return validate;
}

export default useValidateTransferAssetFlow;
